/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { CreditCardError, CreditCardForm } from '@/components';
import {
  CardType,
  CreditCard as PaymentServicesCreditCard,
  CreditCardFieldName,
  CreditCardFormState,
  CreditCardRenderConfig,
} from '@adobe-commerce/payment-services-sdk/payment';
import { useText } from '@adobe-commerce/elsie/i18n';
import * as signals from '@/lib/signals';
import {
  RefObject,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'preact/compat';
import { asError } from '@/lib/error';
import LocalizedError, { fromPaymentsSDKError } from '@/lib/localizedError';
import { PromiseWithResolvers, withResolvers } from 'radashi';

// TODO: We should ideally move this to the Payments SDK (NPM package). It already
//   ... exists as a type definition, but that does not give us the enum values.
//   ... See 'CardType' from @adobe-commerce/payment-services-sdk/payment.
export enum CardTypes {
  Visa = 'visa',
  MasterCard = 'mastercard',
  Amex = 'amex',
  Discover = 'discover',
  Maestro = 'maestro',
  Diners = 'diners',
}

export enum FormFields {
  NUMBER = 'number',
  EXPIRATION_DATE = 'expirationDate',
  CVV = 'cvv',
}

export interface CreditCardProps {
  /**
   * Should return a promise that resolves to the shopper`s cart ID.
   */
  getCartId: () => Promise<string>;
  /**
   * Credit card form reference. Initially, { current: null } should be passed. Once rendered, the credit card
   * container will set the 'current' property to a { validate: () => boolean; submit: () => Promise<void> } object,
   * which parent containers should use to (programmatically) validate and submit the credit card form.
   */
  creditCardFormRef: RefObject<CreditCardFormRef>;
  /**
   * Called when payment flow is successful.
   */
  onSuccess: (result: { cartId: string }) => void;
  /**
   * Called when the payment flow was aborted due to an error.
   *
   * The function receives an object with two properties, { name: string, message: string }, containing the localized
   * error name and message. Both properties are user-facing and can be translated using the
   * "PaymentServices.CreditCard.errors" language definitions.
   */
  onError: (localizedError: LocalizedError) => void;
}

export interface CreditCardFormRef {
  /**
   * Returns true only if all credit card form inputs are valid, and focuses the first
   * input that is invalid, if any.
   */
  validate: () => boolean;
  /**
   * Use this method to submit the credit card form and initiate the payment flow.
   */
  submit: () => Promise<void>;
}

export const CreditCard = ({
  getCartId,
  creditCardFormRef,
  onSuccess,
  onError,
  ...props
}: CreditCardProps) => {
  const { checkout: sdk } = signals.paymentsSDK.value;
  const location = 'CHECKOUT';
  const cardNumberContainerId = 'number';
  const expirationDateContainerId = 'expirationDate';
  const securityCodeContainerId = 'cvv';
  const cardContainerId = `${location}-card-container`;

  const creditCard = useRef<PaymentServicesCreditCard>();
  const paymentFlowPromiseRef = useRef<PromiseWithResolvers<void> | null>(null);

  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [eligibleCards, setEligibleCards] = useState<CardType[]>([]);
  const [selectedCardType, setSelectedCardType] = useState<CardType | null>(
    null
  );

  const [focusedFieldStates, setFocusedFieldStates] = useState<
    // NOTE: Keep track of focused fields only to avoid showing validation errors
    //   ... for all fields when the form is rendered for the first time.
    Partial<CreditCardFormState>
  >({});

  useImperativeHandle(
    creditCardFormRef,
    () => {
      return {
        submit: async () => {
          if (!creditCard.current) {
            return Promise.resolve();
          }
          paymentFlowPromiseRef.current = withResolvers();

          try {
            await Promise.all([
              paymentFlowPromiseRef.current.promise,
              creditCard.current.submit(),
            ]);
          } catch (unknownError) {
            if (unknownError instanceof LocalizedError) {
              throw unknownError; // Already localized; nothing to do
            }
            throw fromPaymentsSDKError('CreditCard', asError(unknownError));
          } finally {
            paymentFlowPromiseRef.current = null;
          }
        },
        validate: () => {
          if (!creditCard.current) {
            return false;
          }

          // Show validation errors for all invalid fields
          const formState = creditCard.current.getFormState();
          setFocusedFieldStates(formState);

          // Focus first invalid field
          const fields = ['number', 'expirationDate', 'cvv'];
          for (const field of fields as CreditCardFieldName[]) {
            if (!formState[field].isValid) {
              creditCard.current.focusField(field);
              return false;
            }
          }

          // All fields valid
          return true;
        },
      };
    },
    []
  );

  const {
    methodNotAvailableMessage,
    methodNotLoadedMessage,
    expirationDatePlaceholder,
    missingExpirationDateError,
    invalidExpirationDateError,
    securityCodePlaceholder,
    missingSecurityCodeError,
    invalidSecurityCodeError,
    cardNumberPlaceholder,
    missingCreditCardNumberError,
    invalidCreditCardNumberError,
  } = useText({
    methodNotAvailableMessage: 'PaymentServices.messages.methodNotAvailable',
    methodNotLoadedMessage: 'PaymentServices.messages.methodNotLoaded',
    expirationDatePlaceholder:
      'PaymentServices.CreditCard.formFields.expirationDate.placeholder',
    missingExpirationDateError:
      'PaymentServices.CreditCard.formFields.expirationDate.missingError',
    invalidExpirationDateError:
      'PaymentServices.CreditCard.formFields.expirationDate.invalidError',
    securityCodePlaceholder:
      'PaymentServices.CreditCard.formFields.cvv.placeholder',
    missingSecurityCodeError:
      'PaymentServices.CreditCard.formFields.cvv.missingError',
    invalidSecurityCodeError:
      'PaymentServices.CreditCard.formFields.cvv.invalidError',
    cardNumberPlaceholder:
      'PaymentServices.CreditCard.formFields.number.placeholder',
    missingCreditCardNumberError:
      'PaymentServices.CreditCard.formFields.number.missingError',
    invalidCreditCardNumberError:
      'PaymentServices.CreditCard.formFields.number.invalidError',
  });

  const validityErrors = {
    [FormFields.NUMBER]: {
      missing: missingCreditCardNumberError,
      invalid: invalidCreditCardNumberError,
    },
    [FormFields.EXPIRATION_DATE]: {
      missing: missingExpirationDateError,
      invalid: invalidExpirationDateError,
    },
    [FormFields.CVV]: {
      missing: missingSecurityCodeError,
      invalid: invalidSecurityCodeError,
    },
  };

  const renderConfig: CreditCardRenderConfig = useMemo(() => {
    return {
      getCartId,
      onSuccess: (result) => {
        paymentFlowPromiseRef.current?.resolve();
        onSuccess?.(result);
      },
      onError: (error) => {
        if (onError === undefined) {
          console.error(error);
        }
        const localizedError = fromPaymentsSDKError('CreditCard', error);
        paymentFlowPromiseRef.current?.reject(localizedError);
        onError?.(localizedError);
      },
      onRender: async (creditCard) => {
        setFieldsLoading(false);
        setEligibleCards(
          creditCard.getEligibleCards().filter((card) =>
            // Keep only credit card types known to the drop-in
            Object.values(CardTypes).includes(card.code as CardTypes)
          )
        );
      },
      onValidityChange: (
        formState: CreditCardFormState,
        emittedBy: CreditCardFieldName
      ) => {
        setFocusedFieldStates((prevState) => {
          return {
            ...prevState,
            [emittedBy]: formState[emittedBy],
          };
        });
      },
      onCardTypeChange: (cardTypes) => {
        return cardTypes.length > 0
          ? setSelectedCardType(cardTypes[0])
          : setSelectedCardType(null);
      },
      disableSubmitOnEnter: true,
      fields: {
        [FormFields.NUMBER]: {
          selector: `#${cardContainerId} #${cardNumberContainerId}`,
          placeholder: cardNumberPlaceholder,
        },
        [FormFields.EXPIRATION_DATE]: {
          selector: `#${cardContainerId} #${expirationDateContainerId}`,
          placeholder: expirationDatePlaceholder,
        },
        [FormFields.CVV]: {
          selector: `#${cardContainerId} #${securityCodeContainerId}`,
          placeholder: securityCodePlaceholder,
        },
      },
    };
  }, [
    cardContainerId,
    cardNumberPlaceholder,
    expirationDatePlaceholder,
    getCartId,
    onError,
    onSuccess,
    securityCodePlaceholder,
  ]);

  useEffect(() => {
    if (!sdk?.Payment.CreditCard.isAvailable()) return;

    sdk.Payment.CreditCard.render(renderConfig)
      .then((instance) => {
        creditCard.current = instance;
      })
      .catch((error) => {
        console.error('Credit Card render failed:', error);
      })
      .finally(() => {
        setFieldsLoading(false);
      });

    return () => {
      if (creditCard.current) {
        creditCard.current.teardown().catch((error) => {
          console.warn('Error tearing down credit card container.', error);
        });
      }
    };
  }, [renderConfig, sdk]);

  if (sdk && !sdk.Payment.CreditCard.isAvailable()) {
    return (
      <div class="payment-services_paypal-credit-card-container">
        <CreditCardError message={methodNotAvailableMessage} />
      </div>
    );
  }

  if (signals.status.value === 'error') {
    return (
      <div class="payment-services_paypal-credit-card-container">
        <CreditCardError message={methodNotLoadedMessage} />
      </div>
    );
  }

  const validationErrors = Object.entries(focusedFieldStates).reduce(
    (acc, [field, state]) => {
      if (!state.isEmpty && state.isValid) return acc;
      acc[field as FormFields] = state.isEmpty
        ? validityErrors[field as FormFields].missing
        : validityErrors[field as FormFields].invalid;
      return acc;
    },
    {} as Record<FormFields, string>
  );

  return (
    <div class="payment-services_paypal-credit-card-container" {...props}>
      <CreditCardForm
        cardContainerId={cardContainerId}
        cardNumberContainerId={cardNumberContainerId}
        expirationDateContainerId={expirationDateContainerId}
        securityCodeContainerId={securityCodeContainerId}
        eligibleCardTypes={eligibleCards}
        isLoading={fieldsLoading}
        selectedCardType={selectedCardType}
        validationErrors={validationErrors}
      />
    </div>
  );
};
